import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { flocknoteKeys } from "Flocknote/API/keys";
import { genericError } from "Flocknote/constants";
import { useUserID } from "Flocknote/stateHooks";

import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";

import { useTwoFAContext } from "../TwoFA";
import { LAST_EMAIL_PHONE, variants } from "../constants";

import { postTwoFactorAuth, postUpdateName } from "./fetch";
import { twoFALoginKeys } from "./keys";

const loginEndpoint = "/login/twoFactorAuth";
const giveEndpoint = `/give/twoFactorAuth`;
const twoFactorFlexibleAuth = "/login/twoFactorFlexibleAuth";

const useClearError = ({ variant }) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const { message } =
      queryClient.getQueryData(twoFALoginKeys[variant]()) || {};
    if (message)
      queryClient.setQueryData(twoFALoginKeys[variant](), {
        ...queryClient.getQueryData(twoFALoginKeys[variant]()),
        message: null,
      });
  }, [queryClient, variant]);
};

const getQueryFn = ({
  queryClient,
  clearError,
  endpoint,
  postData,
  variant,
}) => {
  return async () => {
    const initialLoad =
      queryClient.getQueryState(twoFALoginKeys[variant]()).dataUpdateCount ===
      0;
    if (!initialLoad) clearError();
    try {
      return await postTwoFactorAuth({
        endpoint,
        payload: {
          ...postData,
          initialLoad,
          logout: initialLoad ? false : postData.logout, // never log out on initial load. https://flocknote.monday.com/boards/6445977211/pulses/6700399644
        },
      });
    } catch {
      return {
        ...queryClient.getQueryData(twoFALoginKeys[variant]()),
        message: genericError,
      };
    }
  };
};

const useGiveLogout = ({ fundID, tabID }) => {
  const queryClient = useQueryClient();
  const [, setLastFirstFactorStorage] = useLocalStorage(LAST_EMAIL_PHONE);

  return useMutation({
    mutationFn: async () => {
      // set success to false so the onSuccess funtion does not run.
      queryClient.setQueryData(twoFALoginKeys[variants.give](), {
        loginSucess: false,
      });
      // If the user is intentionally logging out, we assume they will not use the same username
      setLastFirstFactorStorage({});
      const res = await postTwoFactorAuth({
        payload: { logout: true, fundID, tabID },
        endpoint: giveEndpoint,
      });
      queryClient.setQueryData(twoFALoginKeys[variants.give](), res);
      queryClient.invalidateQueries(flocknoteKeys.user());
    },
  });
};

/**
 * @param {TwoFARequest} postData
 */
const usePostTwoFactorAuth = ({ postData, variant }) => {
  const queryClient = useQueryClient();
  const clearError = useClearError({ variant });

  const isLogin = variant === variants.login;
  const isGive = variant === variants.give;
  const isSignList = variant === variants.signupList;
  const isMmg = variant === variants.mmg;
  const isFamReg = variant === variants.famReg;
  const postDataIsReady = Boolean(postData);

  const createQueryFn = useCallback(
    (endpointArg) => {
      return getQueryFn({
        queryClient,
        clearError,
        endpoint: endpointArg,
        postData,
        variant,
      });
    },
    [clearError, postData, queryClient, variant]
  );

  const login = useQuery({
    queryKey: twoFALoginKeys.login(),
    queryFn: createQueryFn(loginEndpoint),
    staleTime: Infinity,
    enabled: isLogin && postDataIsReady,
  });

  const give = useQuery({
    queryKey: twoFALoginKeys.give(),
    queryFn: createQueryFn(giveEndpoint),
    staleTime: Infinity,
    enabled: isGive && postDataIsReady,
  });

  const signupList = useQuery({
    queryKey: twoFALoginKeys.signupList(),
    queryFn: createQueryFn(twoFactorFlexibleAuth),
    staleTime: Infinity,
    enabled: isSignList && postDataIsReady,
  });

  const mmg = useQuery({
    queryKey: twoFALoginKeys.mmg(),
    queryFn: createQueryFn(twoFactorFlexibleAuth),
    staleTime: Infinity,
    enabled: isMmg && postDataIsReady,
  });

  const famReg = useQuery({
    queryKey: twoFALoginKeys.famReg(),
    queryFn: createQueryFn(twoFactorFlexibleAuth),
    staleTime: Infinity,
    enabled: isFamReg && postDataIsReady,
  });

  if (isSignList) return signupList;
  if (isGive) return give;
  if (isMmg) return mmg;
  if (isFamReg) return famReg;
  return login;
};

const usePostUpdateName = ({ onSuccess }) => {
  const userID = useUserID();
  const { fullName } = useTwoFAContext();

  const updateName = useMutation({
    mutationKey: twoFALoginKeys.updateName(userID),
    mutationFn: () => postUpdateName({ userID, fullName }),
    onSuccess,
  });

  return updateName;
};

export {
  usePostTwoFactorAuth,
  usePostUpdateName,
  useClearError,
  useGiveLogout,
};
